/**
 * Sticky add-to-cart bar.
 *
 * Mirrors the main product form rather than owning its own form, so the
 * currently selected variant, sold-out state and quantity all stay in sync
 * without duplicating Dawn's variant logic.
 */
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.mainForm = document.querySelector('product-form form[action*="/cart/add"]');
    this.mainButton = this.mainForm ? this.mainForm.querySelector('[name="add"]') : null;

    if (!this.mainForm || !this.mainButton) {
      // No product form on the page (e.g. a preview) — leave the bar hidden.
      return;
    }

    this.stickyButton = this.querySelector('[data-sticky-add]');
    this.priceContainer = this.querySelector('[data-sticky-price]');
    this.sourcePrice = document.querySelector('.product__info-container .price');

    if (this.dataset.showOnMobile === 'false') {
      this.classList.add('sticky-atc--desktop-only');
    }

    this.bindAddToCart();
    this.watchMainButton();
    this.observeButtonState();
    this.observePrice();
    this.syncButtonState();
  }

  disconnectedCallback() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.stateObserver) this.stateObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
  }

  bindAddToCart() {
    this.stickyButton.addEventListener('click', () => {
      if (this.mainButton.getAttribute('aria-disabled') === 'true' || this.mainButton.disabled) return;

      // requestSubmit fires the submit event so Dawn's product-form handler
      // (cart drawer / notification) runs exactly as it would normally.
      if (typeof this.mainForm.requestSubmit === 'function') {
        this.mainForm.requestSubmit();
      } else {
        this.mainButton.click();
      }
    });
  }

  /**
   * Show the bar only once the real Add to cart button has scrolled out of
   * view. A scroll listener (rAF-throttled) is used rather than an
   * IntersectionObserver so this still works when IO callbacks are throttled,
   * e.g. in background tabs or embedded preview frames.
   */
  watchMainButton() {
    // One getBoundingClientRect per scroll event on a passive listener is
    // cheap, and unlike rAF / IntersectionObserver it keeps working when the
    // rendering pipeline is throttled.
    this.onScroll = () => {
      const rect = this.mainButton.getBoundingClientRect();
      // Out of view = scrolled past the top, or not yet reached at the bottom.
      const outOfView = rect.bottom < 0 || rect.top > window.innerHeight;
      this.hidden = !outOfView;
      this.classList.toggle('sticky-atc--visible', outOfView);
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  /** Keep label + disabled state in step with the main button. */
  observeButtonState() {
    this.stateObserver = new MutationObserver(() => this.syncButtonState());
    this.stateObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
    });
  }

  syncButtonState() {
    const disabled =
      this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';

    this.stickyButton.disabled = disabled;
    this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

    const label = this.mainButton.querySelector('span');
    if (label && label.textContent.trim()) {
      this.stickyButton.textContent = label.textContent.trim();
    }
  }

  /** Mirror the main price block whenever the variant changes. */
  observePrice() {
    if (!this.sourcePrice || !this.priceContainer) return;

    const copyPrice = () => {
      this.priceContainer.innerHTML = this.sourcePrice.innerHTML;
    };

    this.priceObserver = new MutationObserver(copyPrice);
    this.priceObserver.observe(this.sourcePrice, { childList: true, subtree: true, characterData: true });
    copyPrice();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
